import React from "react";
import { Package } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Button from "../UI/Button";
import { useAppDispatch } from "../../hooks/useStore";
import { useModules } from "../../hooks/useModules";
import { Task } from "../../types/tasks";
import { createShipmentFromTask } from "../../store/slices/tasksSlice";
import { MOCK_TMS_ENABLED } from "../../constants/taskConstants";

export interface ShipmentValidationErrors {
  clientId?: string;
  pickupCity?: string;
  deliveryCity?: string;
}

interface Props {
  task: Task | null;
  draft: Partial<Task>;
  onCreated: (shipmentId: string, shipmentNumber: string) => void;
  onValidationErrors?: (errors: ShipmentValidationErrors) => void;
  disabled?: boolean;
}

/** Button is visible whenever useStructuredForm is ON and the task is persisted.
 *  Validation (client + pickup city + delivery city) runs on click. */
const isVisible = (task: Task | null, draft: Partial<Task>): boolean => {
  if (!MOCK_TMS_ENABLED) return false;
  if (!task?._id) return false; // must be a saved task
  if (!draft.useStructuredForm) return false;
  return true;
};

export const CreateShipmentButton: React.FC<Props> = ({
  task,
  draft,
  onCreated,
  onValidationErrors,
  disabled,
}) => {
  // This button reaches into the shipments module from a task form, so it has
  // to check that module rather than the one the page belongs to — a tenant
  // running tasks without shipments would otherwise get a button whose API
  // answers 404.
  const { has } = useModules();
  const { t } = useTranslation("tasks");
  const dispatch = useAppDispatch();

  if (!task) return null;
  if (task.linkedShipmentId || draft.linkedShipmentId) return null;
  if (!isVisible(task, draft)) return null;
  if (!has("shipments")) return null;

  const handleClick = async () => {
    const tr = draft.transport;
    const pickupCity = tr?.pickupCity || tr?.pickupAddress;
    const deliveryCity = tr?.deliveryCity || tr?.deliveryAddress;

    const errors: ShipmentValidationErrors = {};
    if (!draft.clientId && !draft.clientName) {
      errors.clientId = t("shipment.validationClientRequired");
    }
    if (!pickupCity) {
      errors.pickupCity = t("shipment.validationPickupCity");
    }
    if (!deliveryCity) {
      errors.deliveryCity = t("shipment.validationDeliveryCity");
    }

    if (Object.keys(errors).length > 0) {
      onValidationErrors?.(errors);
      toast.error(t("shipment.validationCheckFields"));
      return;
    }

    // Clear any previous errors
    onValidationErrors?.({});

    try {
      const result = await dispatch(
        createShipmentFromTask({
          taskId: task._id,
          transport: draft.transport as unknown as Record<string, unknown>,
          clientId: draft.clientId ?? null,
          clientName: draft.clientName ?? null,
        }),
      ).unwrap();
      toast.success(t("shipment.successToast"));
      onCreated(result.shipmentId, result.shipmentNumber);
    } catch (err) {
      if (err === "validation") {
        toast.error(t("shipment.validationError"));
      }
    }
  };

  return (
    <Button
      type="button"
      variant="secondary"
      onClick={handleClick}
      disabled={disabled}
      icon={<Package className="w-4 h-4" />}
      title={t("shipment.createButton")}
    >
      {/* Show full label on sm+, icon-only on mobile to save footer space */}
      <span className="hidden sm:inline">{t("shipment.createButton")}</span>
    </Button>
  );
};
